/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, useContext } from "react"
import collectionService from "../services/collection"
import { retrieveItemImageFromHero } from "../components/utilities"
import { collectionFullSlugPrefixer } from "../helper/collectionFullSlugPrefixer"

import PageContext from "../components/page-context"
import { t } from "../i18n-utility"

const useRemoteHomepageFeedHandler = (options) => {
  const { translations, pageUid } = useContext(PageContext)
  const latestFeedServiceObj = new collectionService(
    Object.assign(options, {
      pageUid: pageUid,
      apiEndpoint: process.env.REMOTE_HOMEPAGE_FEED_LATEST_API_ENDPOINT,
    })
  )

  const specialsFeedServiceObj = new collectionService(
    Object.assign(options, {
      pageUid: pageUid,
      apiEndpoint: process.env.REMOTE_HOMEPAGE_FEED_SPECIALS_API_ENDPOINT,
    })
  )

  const [totalCount, setTotalCount] = useState(null)
  const [collectionForPage, setCollectionForPage] = useState([])
  const [availableFilterOptions, setAvailableFilterOptions] = useState({})
  const [activePage, setActivePage] = useState(null)
  const [hasNextPage, setHasNextPage] = useState(false)

  const setSelectedFilterOptions = async (options) => {
    let activeFilterOptions = []
    if (options.formats.length) {
      activeFilterOptions.push({
        user_selected_formats: options.formats.join(","),
      })
    }
    if (options.root_categories.length) {
      activeFilterOptions.push({
        user_selected_root_categories: options.root_categories.join(","),
      })
    }
    latestFeedServiceObj.setUserFilterParams(activeFilterOptions)
    // start with page 1 when filter options are freshly set
    const newItems = await loadPage(1)
    setActivePage(1)
    setCollectionForPage(newItems)
  }

  const prepareAvailableFilterOptions = (filterableValues) => {
    let preparedAvailableFilterOptions = {}

    if (filterableValues.formats && filterableValues.formats.length > 0) {
      preparedAvailableFilterOptions["formats"] = filterableValues.formats.map(
        (format) => {
          return {
            uid: format,
            title: t(translations, `filters.${format}`, true),
          }
        }
      )
    }

    if (
      filterableValues.root_categories &&
      filterableValues.root_categories.length > 0
    ) {
      preparedAvailableFilterOptions["root_categories"] =
        filterableValues.root_categories
    }
    setAvailableFilterOptions(preparedAvailableFilterOptions)
  }

  const loadPage = async (page) => {
    try {
      let collectionResult =
        await latestFeedServiceObj.getCollectionForSinglePage(page)
      collectionResult = retrieveItemImageFromHero(collectionResult)
      setHasNextPage(latestFeedServiceObj.lastRequestMeta.has_next_page)
      setTotalCount(latestFeedServiceObj.lastRequestMeta.total)

      if (page === 1 && latestFeedServiceObj.filterableValues) {
        prepareAvailableFilterOptions(latestFeedServiceObj.filterableValues)
      }

      if (page === 1 && specialsFeedServiceObj) {
        let specialsResult =
          await specialsFeedServiceObj.getCollectionForSinglePage(1)
        specialsResult = retrieveItemImageFromHero(specialsResult)

        let specialsItem = {
          type: "highlightArticles",
          title: specialsFeedServiceObj.lastRequestHeader.title,
          description: specialsFeedServiceObj.lastRequestHeader.description,
          collection: specialsResult,
        }
        collectionResult.splice(3, 0, specialsItem)
      }

      return collectionResult
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const addItems = (newItems) => {
    if (collectionForPage.length > 0) {
      setCollectionForPage(collectionForPage.concat(newItems))
    }
  }

  const handleNextPage = async () => {
    const nextPage = activePage + 1
    const newItems = await loadPage(nextPage)
    setActivePage(nextPage)
    addItems(newItems)
  }

  const handleFirstPage = async () => {
    const newItems = await loadPage(1)
    setActivePage(1)
    setCollectionForPage(newItems)
  }

  useEffect(() => {
    if (
      collectionForPage &&
      collectionForPage.length &&
      window.upgradeAllBehaviours
    ) {
      // ensure grid and images are initialized by ui kit
      window.upgradeAllBehaviours()
    }
  }, [collectionForPage])

  // init
  useEffect(() => {
    handleFirstPage()
  }, [])

  return {
    collectionForPage,
    activePage,
    hasNextPage,
    totalCount,
    availableFilterOptions,
    setSelectedFilterOptions,
    handleNextPage,
  }
}

export default useRemoteHomepageFeedHandler
